import React, { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { AuthContext } from '../../context/authContext';
import { checkNIP, getLogPoints, logoutUser, updatePointEmployee } from '../../utilities/sendRequest';
import { dateTimeIndo } from '../../utilities/helper';
import { PopupContext } from '../../context/popupContext';
import ModalPointDetail from '../../components/LogPoint/modalPoint';
import { typeOptions } from '../../utilities/selectOptions';

const LogPoints = () => {
    const { dispatch } = useContext(AuthContext)
    const { popupDispatch } = useContext(PopupContext)
    const [isReady, setIsReady] = useState(false)
    const [datas, setDatas] = useState([])
    const [data, setData] = useState([])
    const [showModal, setShowModal] = useState(false)
    const [fetchMore, setFetchMore] = useState(true)

    const [isChecking, setIsCheking] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [debouncedCode, setDebouncedCode] = useState("")

    const [point, setPoint] = useState("")
    const [type, setType] = useState("")
    const [nip, setNip] = useState("")
    const [fullname, setFullname] = useState("")
    const [employeeId, setEmployeeId] = useState("")
        
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [convertedContent, setConvertedContent] = useState(null);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedCode(nip);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [nip]);

    useEffect(() => {
        if (!debouncedCode) {
            setIsValid(true)
            setIsCheking(false)
            return;
        }
        const handleCode = async () => {
            setIsLoading(true)

            checkNIP(debouncedCode).then((res) => {
                if (res.payload.is_valid) {
                    setIsValid(true)
                    setFullname(res.payload.fullname)
                    setEmployeeId(res.payload.id)
                } else {
                    setIsValid(false)
                    setFullname("")
                    setEmployeeId("")
                }
            }).catch((err) => {
                console.log(err)
                setIsValid(false)
                setFullname("")
                setEmployeeId("")
            }).finally(() => {
                setIsLoading(false)
                setIsCheking(true)
            })
        }
        handleCode()
    }, [debouncedCode]);

    useEffect(() => {
        let html = convertToHTML(editorState.getCurrentContent());
        setConvertedContent(html);
    
    }, [editorState]);
    
    useEffect(() => {
        const isReady = (point !== "" && type !== "" && employeeId !== "" && isValid === true);
        setIsReady(isReady);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [point, type, employeeId, isValid])

    useEffect(() => {
        fetchLogs()
        setFetchMore(false)
        
        // eslint-disable-next-line
    }, [fetchMore])

    const fetchLogs = async () => {
        getLogPoints().then((res) => {
            setDatas(res.payload.sort((p1, p2) => {
                return new Date(p2.createdAt) - new Date(p1.createdAt)
            }))
        }).catch((error) => {
            toast.error(error.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });

            if (error.response.status === 401) {
                setTimeout(() => {
                    logoutUser(dispatch)
                }, 3500)
            }
        })
    }

    const columns = [
        { 
            field: 'createdAt', 
            headerName: 'Time', 
            width: 150, 
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {dateTimeIndo(params.row.createdAt)}
                    </div>
                )
            } 
        },
        { 
            field: 'Obtained_Point_Log', 
            headerName: 'Employee',
            width: 300, 
            renderCell: (params) => {
                return (
                    <div className='flex justify-center items-center font-ssp'>
                        {params.row.Obtained_Point_Log?.fullname+' - '+params.row.Obtained_Point_Log?.nip}
                    </div>
                )
            } 
        },
        { 
            field: 'Approved_Point_Log', 
            headerName: 'Admin',
            width: 250, 
            renderCell: (params) => {
                return (
                    <div className='flex justify-center items-center font-ssp'>
                        {params.row.Approved_Point_Log?.fullname}
                    </div>
                )
            } 
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 150,
            renderCell: (params) => {
                return (
                    <div className="font-ssp">
                        {params.row.type === 'revenue'
                            ? <span className="px-2 py-1 bg-green-400 rounded-full text-white uppercase">PENAMBAHAN</span>
                            : <span className="px-2 py-1 bg-red-400 rounded-full text-white uppercase">PENGURANGAN</span>
                        }
                    </div>
                )
            } 
        },
        { 
            field: 'point', 
            headerName: 'Point',
            width: 200, 
            renderCell: (params) => {
                return (
                    <div className='flex justify-center items-center font-ssp'>
                        {params.row.point}
                    </div>
                )
            } 
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 150,
            renderCell: (params) => {
                return (
                    <button
                        onClick={() => handleModal(params.row)}
                        className='w-full flex flex-row gap-3 items-center'>
                        <VisibilityIcon fontSize='medium' className="text-slate-400 hover:text-slate-500 cursor-pointer"/>
                    </button>
                )
            }
        },
    ]

    const handleModal = (data) => {
        setShowModal(true)
        setData(data)
        popupDispatch({ type: "POPUP_START" });
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        setIsReady(false)
    
        const reqData = {
            point,
            description: convertedContent,
            type
        }
    
        updatePointEmployee(nip, reqData).then((res) => {
            toast.success(res.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });  
            
            setNip("")
            setFullname("")
            setEmployeeId("")
            setType("")
            setPoint("")
            setConvertedContent(null)
            setIsValid(false)
            setEditorState(EditorState.createEmpty())
            
        }).catch((err) => {
            toast.error(err.response.data.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 3000
            });
        }).finally(() => {
            setIsReady(true)
            setFetchMore(true)
        })
    }

    return (
        <>
            {showModal && 
                <ModalPointDetail setShowModal={setShowModal} data={data} setFetchMore={setFetchMore} />
            }
            <div className={`min-h-[calc(100vh-4rem)] flex flex-col ${showModal && 'fixed min-w-[calc(100vw-16.666667%)]'} `}>
                {/* header  */}
                <div className="my-5 mb-5 mx-7 md:mx-5 flex flex-col md:flex-row justify-between">
                    <h1 className='font-ssp font-bold text-2xl md:text-3xl'>Points</h1>
                </div>
                {/* content  */}
                <div className="my-5 mb-5 mx-7 md:mx-5 flex flex-col gap-10">
                    <div className="p-2 w-full flex bg-white drop-shadow-lg rounded-md">
                        <form onSubmit={handleSubmit} className='mt-5 px-5 w-full flex flex-col'>
                            <div className='flex flex-wrap justify-between'>
                                {/* employee */}
                                <div className="relative z-0 mb-3 mt-3 w-full md:w-[45%] group">
                                    <input 
                                        type="text" 
                                        name="employee" 
                                        id="employee" 
                                        value={nip} onChange={(e) => setNip(e.target.value)}
                                        className={`block py-2.5 px-2 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 
                                                    appearance-none focus:outline-none focus:ring-0 ${isChecking ? isLoading ? 'focus:border-yellow-600' : isValid ? 'focus:border-green-600' : 'focus:border-red-600' : 'focus:border-gray-400'} peer`} 
                                        placeholder=" " 
                                        required 
                                    />
                                    <label 
                                        htmlFor="employee" 
                                        className={`absolute top-3 -z-10 text-sm text-gray-500 dark:text-gray-400 
                                                    duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:font-medium
                                                    peer-focus:left-0 ${isChecking ? isLoading ? 'peer-focus:border-yellow-600' : isValid ? 'peer-focus:border-green-600' : 'peer-focus:border-red-600' : 'peer-focus:border-gray-400'} peer-placeholder-shown:scale-100 
                                                    peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6`}
                                    >NIP</label>
                                </div>
                                <div className="relative z-0 mb-3 mt-3 w-full md:w-[45%] group">
                                    <input 
                                        value={fullname} onChange={(e) => setFullname(e.target.value)}
                                        type="text" 
                                        name="employee_name" 
                                        id="employee_name" 
                                        readOnly
                                        className={`block py-2.5 px-2 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 
                                                    appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer`}
                                        placeholder=" " 
                                        required 
                                    />
                                    <label 
                                        htmlFor="employee_name" 
                                        className="absolute top-3 -z-10 text-sm text-gray-500 dark:text-gray-400 
                                                    duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:font-medium
                                                    peer-focus:left-0 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 
                                                    peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                    >Employee</label>
                                </div>
                            </div>
                            <div className='flex flex-wrap justify-between'>
                                {/* point */}
                                <div className="relative z-0 mb-3 mt-3 w-full md:w-[45%] group">
                                    <input 
                                        value={point} 
                                        onChange={(e) => setPoint(e.target.value)} 
                                        type="number" 
                                        name="point" 
                                        id="point" 
                                        className="block py-2.5 px-2 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 
                                                    appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer" 
                                        placeholder=" " 
                                        required 
                                    />
                                    <label 
                                        htmlFor="point" 
                                        className="absolute top-3 -z-10 text-sm text-gray-500 dark:text-gray-400 
                                                    duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:font-medium
                                                    peer-focus:left-0 peer-focus:text-blue-600 peer-placeholder-shown:scale-100 
                                                    peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                    >Point</label>
                                </div>
                                {/* type */}
                                <div className="mb-3 mt-4 w-full md:w-[45%] relative z-0 group">
                                    <select required value={type} 
                                        onChange={(e) => setType(e.target.value)} 
                                        id="underline_select" 
                                        className="block py-2.5 px-2 w-full text-sm text-gray-500 bg-transparent 
                                                    border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 
                                                    focus:border-blue-600 peer"
                                    >
                                        {typeOptions.map((option) => (
                                            <option key={option.value} value={option.value} >{option.label} </option>
                                        ))}
                                    </select>
                                    <label 
                                        htmlFor="underline_select" 
                                        className="absolute top-3 -z-10 text-sm text-gray-500 
                                                    duration-300 transform -translate-y-6 scale-75 origin-[0] peer-focus:left-0 peer-focus:font-medium 
                                                    peer-focus:text-blue-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 
                                                    peer-focus:scale-75 peer-focus:-translate-y-6"
                                    >Type</label>
                                </div>
                            </div>
                            {/* description */}
                            <div className="mt-3 mb-6 w-full relative z-0 group">
                                <label 
                                    htmlFor="description" 
                                    className="text-sm text-gray-500 duration-300 transform -translate-y-6 
                                                scale-75 origin-[0] peer-focus:left-0 peer-focus:text-blue-600 peer-focus:font-medium 
                                                peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                >Description</label>
                                <Editor
                                    editorState={editorState}
                                    onEditorStateChange={setEditorState}
                                    wrapperClassName="mt-2 border h-[500px] md:h-[300px] relative flex flex-col overflow-hidden border border-gray-300"
                                    toolbarClassName="sticky"
                                    editorClassName="px-2 overflow-y-scroll"
                                />
                            </div>
                            {/* Button */}
                            <div className="mb-6 mt-5 w-full relative z-0 flex gap-3 justify-end">
                                <button type="submit" 
                                    disabled={!isReady}
                                    className={`mt-5 px-5 py-2.5 w-full sm:w-auto ${isReady ? 'text-white bg-blue-400 hover:bg-blue-500 cursor-pointer' : 'text-gray-800 bg-gray-300 cursor-wait'} rounded-lg text-sm
                                                text-center font-medium`}
                                >Save</button>
                                <button onClick={handleModal} 
                                    className="px-5 py-2.5 mt-5 w-full sm:w-auto bg-red-400 hover:bg-red-500 rounded-lg text-sm text-white
                                    text-center font-medium cursor-pointer"
                                >Close</button>
                            </div>
                        </form>
                    </div>
                    <div className="p-2 w-full flex bg-white drop-shadow-lg rounded-md">
                        <DataGrid
                            autoHeight {...datas}
                            rows={datas}
                            columns={columns}
                            pageSize={10}
                            getRowId={(row) => row.id}
                            rowsPerPageOptions={[10]}
                            checkboxSelection
                            disableSelectionOnClick
                            components={{ Toolbar: GridToolbar, }}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default LogPoints